import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fmtDate } from '@dcc-erp/shared';
import type { ArCreditNote } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApproveArCreditNoteStep,
  useArCreditNote,
  useRejectArCreditNoteStep,
  useReturnArCreditNote,
  useSubmitArCreditNote,
} from '../../lib/queries';
import { AppModal } from '../AppModal';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

interface Props {
  companyId: string;
  /** The list row that opened this panel — used for the header before the full
   *  detail (with the approvals chain) resolves. */
  creditNote: ArCreditNote & { customerName?: string | null };
  onClose: () => void;
}

// AR credit-note central-engine 4-eyes panel. Mirrors ApBillApprovalPanel
// exactly: re-fetch the full detail for the materialized approvals chain, render
// the shared ApprovalTimeline, and drive submit/approve/return/reject through
// the doc-type-agnostic DecisionModal. Final-step approval auto-posts the
// reversing JV server-side (Design-Z: accounting `status` flips to POSTED).
export function ArCreditNoteApprovalPanel({ companyId, creditNote, onClose }: Props) {
  const { data: detail } = useArCreditNote(companyId, creditNote.id);
  const view = detail ?? creditNote;

  const submitMut = useSubmitArCreditNote(companyId);
  const approveMut = useApproveArCreditNoteStep(companyId);
  const rejectMut = useRejectArCreditNoteStep(companyId);
  const returnMut = useReturnArCreditNote(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  const approvalStatus = view.approvalStatus ?? null;
  const currentStep = view.currentStep ?? 0;
  // The materialized approval chain only exists on the re-fetched detail.
  const approvals = detail?.approvals ?? [];
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';
  const customerName = detail?.customerName ?? creditNote.customerName ?? null;

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(view.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          id: view.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          id: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['ar-credit-notes', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <AppModal
      title={`สายอนุมัติใบลดหนี้ — ${view.voucherNo}`}
      {...(customerName ? { subtitle: customerName } : {})}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="ar.creditnote.create">
              <button
                className="btn primary"
                disabled={submitBusy}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="ar.creditnote.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </>
      }
    >
      {/* RETURNED reason banner (resubmit context) */}
      {approvalStatus === 'RETURNED' && view.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
          data-testid="ar-cn-returned-reason"
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {view.returnedReason}
        </div>
      )}

      {/* awaiting-step hint */}
      {isPending && (
        <div className="meta" style={{ marginBottom: 12 }}>
          <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
        </div>
      )}

      <ApprovalTimeline
        approvals={approvals}
        isActive={isPending}
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          view.submittedBy ? (
            <div className="meta">
              ส่งอนุมัติโดย {view.submittedBy}
              {view.submittedAt ? ` · ${fmtDate(view.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
        trailLine={
          view.status === 'POSTED' && view.approvedBy ? (
            <div className="meta">
              อนุมัติโดย {view.approvedBy}
              {view.approvedAt ? ` · ${fmtDate(view.approvedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      {actionError && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 10 }}
          data-testid="ar-cn-action-error"
        >
          {actionError}
        </div>
      )}

      {showDecide && (
        <DecisionModal
          voucherNo={view.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาใบลดหนี้"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </AppModal>
  );
}
